import React, { useState, useEffect } from 'react';
import { 
  Plus, CheckCircle, Clock, User, Sparkles,
  Check, X, ChevronRight, Loader2
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import TaskCard from './TaskCard';
import CreateTaskModal from './CreateTaskModal';
import AssignTaskModal from './AssignTaskModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Task Panel - World Class Minimalist Design
 * Matches dashboard aesthetic with clean lines, subtle borders, horizontal stats
 */
const TaskPanel = ({ caseId, currentUserId, onUpdate }: { caseId: any; currentUserId?: any; onUpdate?: any }) => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const t = (en: string, no: string) => isEnglish ? en : no;
  const [tasks, setTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    if (caseId) {
      fetchTasks();
    }
  }, [caseId, filter]);

  const fetchTasks = async () => {
    if (!caseId) {
      console.warn('TaskPanel: No caseId provided');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('TaskPanel: No auth token found');
        toast.error(t('Not authenticated', 'Ikke autentisert'));
        setLoading(false);
        return;
      }
      
      const allTasksResponse = await axios.get(
        `${API}/tasks/${caseId}?filter=all`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAllTasks(allTasksResponse.data.tasks || []);
      
      const response = await axios.get(
        `${API}/tasks/${caseId}?filter=${filter}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTasks(response.data.tasks || []);
      if (response.data.user_role) {
        setUserRole(response.data.user_role);
      }
    } catch (error: any) {
      console.error('Failed to fetch tasks:', error?.response?.data || error);
      const errorMsg = error?.response?.data?.detail || t('Could not load tasks', 'Kunne ikke laste oppgaver');
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (taskId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/tasks/${taskId}/approve`,
        { approved: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Oppgave godkjent!');
      fetchTasks();
    } catch (error) {
      console.error('Failed to approve task:', error);
      toast.error('Kunne ikke godkjenne oppgave');
    }
  };

  const handleReject = async (taskId, reason) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/tasks/${taskId}/approve`,
        { approved: false, rejection_reason: reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Oppgave avvist');
      fetchTasks();
    } catch (error) {
      console.error('Failed to reject task:', error);
      toast.error('Kunne ikke avvise oppgave');
    }
  };

  const handleStatusChange = async (taskId, newStatus, actualHours = null) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/tasks/${taskId}/status`,
        { status: newStatus, actual_hours: actualHours },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status oppdatert');
      fetchTasks();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Kunne ikke oppdatere status');
    }
  };

  const handleAssign = async (taskId, userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/tasks/${taskId}/assign`,
        { assignee_user_id: userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Oppgave tildelt');
      setShowAssignModal(false);
      setSelectedTask(null);
      fetchTasks();
    } catch (error) {
      console.error('Failed to assign task:', error);
      toast.error('Kunne ikke tildele oppgave');
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne oppgaven?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Oppgave slettet');
      fetchTasks();
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast.error('Kunne ikke slette oppgave');
    }
  };

  const isLead = userRole === 'LEAD' || userRole === 'OWNER' || userRole === 'owner';
  
  const pendingCount = allTasks.filter(t => {
    const status = (t.status || '').toUpperCase();
    return status === 'PENDING_APPROVAL';
  }).length;
  
  const myTasksCount = allTasks.filter(t => t.assignee_user_id === currentUserId).length;
  const completedCount = allTasks.filter(t => t.completed).length;
  const openCount = allTasks.filter(t => !t.completed).length;

  return (
    <div className="min-h-full bg-white">
      <div className="max-w-5xl mx-auto px-8 py-10">
        
        {/* Header - Minimalist style */}
        <header className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">{t('Tasks', 'Oppgaver')}</h1>
              <p className="text-sm text-gray-500 mt-1">
                {t('Manage and track tasks for this case', 'Administrer og spor oppgaver for denne saken')}
              </p>
            </div>
            {isLead && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-xl transition-colors"
              >
                <Plus className="w-4 h-4" />
                {t('New task', 'Ny oppgave')}
              </button>
            )}
          </div>

          {/* Stats Row - Clean horizontal layout */}
          <div className="flex items-center gap-12 py-5 border-y border-gray-100">
            <div>
              <div className="text-2xl font-semibold text-gray-900">{allTasks.length}</div>
              <div className="text-xs text-gray-400 mt-0.5">{t('Total', 'Totalt')}</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-emerald-600">{completedCount}</div>
              <div className="text-xs text-gray-400 mt-0.5">{t('Completed', 'Fullført')}</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-gray-900">{openCount}</div>
              <div className="text-xs text-gray-400 mt-0.5">{t('Open', 'Åpne')}</div>
            </div>
            {pendingCount > 0 && (
              <div>
                <div className="text-2xl font-semibold text-amber-600">{pendingCount}</div>
                <div className="text-xs text-gray-400 mt-0.5">{t('Pending approval', 'Godkjenning')}</div>
              </div>
            )}
          </div>
        </header>

        {/* Filter Tabs - Minimal pill style */}
        <div className="flex gap-1 mb-8 bg-gray-100 rounded-xl p-1 w-fit">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
              filter === 'all'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('All', 'Alle')} ({allTasks.length})
          </button>
          <button
            onClick={() => setFilter('mine')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
              filter === 'mine'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('Mine', 'Mine')} ({myTasksCount})
          </button>
          {isLead && pendingCount > 0 && (
            <button
              onClick={() => setFilter('pending')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                filter === 'pending'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('Approval', 'Godkjenning')} ({pendingCount})
            </button>
          )}
          <button
            onClick={() => setFilter('team')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
              filter === 'team'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Team
          </button>
        </div>

        {/* Tasks List */}
        <div>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-gray-900 font-medium mb-1">{t('No tasks yet', 'Ingen oppgaver ennå')}</p>
              <p className="text-sm text-gray-500 mb-4">
                {isLead ? t('Create a task to get started', 'Opprett en oppgave for å komme i gang') : t('Waiting for tasks', 'Venter på oppgaver')}
              </p>
              {isLead && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="text-sm text-gray-900 font-medium hover:underline inline-flex items-center gap-1"
                >
                  {t('Create first task', 'Opprett første oppgave')} <ChevronRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-0">
              {tasks.map((task, index) => (
                <div
                  key={task.id}
                  className="group flex items-center gap-4 py-3 cursor-pointer transition-colors border-b border-gray-50 last:border-b-0 hover:bg-gray-50 -mx-2 px-2 rounded-lg"
                >
                  {/* Completion checkbox */}
                  <button
                    onClick={() => handleStatusChange(task.id, task.completed ? 'pending' : 'completed')}
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      task.completed
                        ? 'bg-emerald-500 border-emerald-500 text-white'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {task.completed && <Check className="w-3 h-3" />}
                  </button>
                  
                  {/* Task content */}
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-medium text-sm ${task.completed ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                      {task.title}
                    </h4>
                    {task.description && (
                      <p className="text-xs text-gray-400 truncate mt-0.5">{task.description}</p>
                    )}
                  </div>
                  
                  {/* Due date */}
                  {task.due_date && (
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(task.due_date).toLocaleDateString('no-NO', { day: 'numeric', month: 'short' })}
                    </span>
                  )}
                  
                  {/* Status badge */}
                  {task.status === 'PENDING_APPROVAL' && (
                    <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                      Godkjenning
                    </span>
                  )}
                  
                  {/* Assignee */}
                  {task.assignee_name && (
                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600" title={task.assignee_name}>
                      {task.assignee_name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  
                  <ChevronRight className="w-4 h-4 text-gray-200 group-hover:text-gray-400 transition-colors" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* AI Suggestion Box - like dashboard */}
        {tasks.length > 0 && tasks.filter(t => !t.completed).length > 0 && (
          <div className="mt-8 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-5 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent" />
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-violet-400" />
                <span className="text-xs font-medium text-gray-400">{t('Lexy suggests', 'Lexy foreslår')}</span>
              </div>
              <p className="text-sm text-gray-300">
                {isEnglish 
                  ? `You have ${tasks.filter(t => !t.completed).length} open tasks.`
                  : `Du har ${tasks.filter(t => !t.completed).length} åpne oppgaver.`
                }
                {tasks.filter(t => !t.completed && t.due_date && new Date(t.due_date) < new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)).length > 0 && 
                  (isEnglish ? ' Some have deadlines soon - prioritize these first.' : ' Noen har frist snart - prioriter disse først.')}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateTaskModal
          caseId={caseId}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchTasks();
          }}
        />
      )}

      {showAssignModal && selectedTask && (
        <AssignTaskModal
          caseId={caseId}
          task={selectedTask}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedTask(null);
          }}
          onAssign={(userId) => handleAssign(selectedTask.id, userId)}
        />
      )}
    </div>
  );
};

export default TaskPanel;
